import ArticleIcon from '@mui/icons-material/Article'
import FolderIcon from '@mui/icons-material/Folder'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { Storage } from 'aws-amplify'
import React, { useContext, useEffect, useState } from 'react'

import {
  ResourcesContext,
  ResourcesContextInterface,
} from '../../providers/Resources/Resources.provider'
import { themeColors } from '../../theme'

export interface ResourceFolder {
  id: number
  name: string
  parentFolderId: number | null
  type: 'folder'
}

export interface ResourceFile {
  id: number
  name: string
  storageKey: string
  folderId: number
  type: 'file'
  isVisibleToPatient?: boolean // Add this property to track visibility
}

export type ResourceItem = ResourceFolder | ResourceFile

export const PatientResourceCheck = ({
  routedPatientId,
}: {
  routedPatientId: string | undefined
}) => {
  const {
    rootResourceFolders,
    getRootResourceFolders,
    getFolderContentById,
    markFileVisibleToPatient,
    unassignFileFromPatients,
    getPatientsAssignedToFile,
  } = useContext(ResourcesContext) as ResourcesContextInterface

  // State to track folders and files
  const [currentFolder, setCurrentFolder] = useState<ResourceFolder | null>(
    null
  )
  const [currentFolderContent, setCurrentFolderContent] = useState<
    ResourceItem[] | null
  >(null)
  const [breadcrumbPath, setBreadcrumbPath] = useState<ResourceFolder[]>([])
  const [selectedFile, setSelectedFile] = useState<ResourceItem | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [updatingVisibility, setUpdatingVisibility] = useState<Set<number>>(
    new Set()
  )
  const [alertMessage, setAlertMessage] = useState<string | null>(null)
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>(
    'success'
  )
  // State to track files' visibility for the current patient
  const [fileVisibility, setFileVisibility] = useState<Map<number, boolean>>(
    new Map()
  )

  // Use refs to track component mounted state to prevent memory leaks
  const isMounted = React.useRef(true)

  // Set isMounted to false when component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (isMounted.current) {
      getRootResourceFolders()
    }
  }, [])

  // Auto-hide alerts after 5 seconds
  useEffect(() => {
    if (alertMessage) {
      const timer = setTimeout(() => {
        setAlertMessage(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [alertMessage])

  const showAlert = (message: string, severity: 'success' | 'error') => {
    setAlertMessage(message)
    setAlertSeverity(severity)
  }

  // Function to load file visibility for current folder
  const loadFileVisibility = async (folderContent: any[]) => {
    if (!routedPatientId) return

    const patientId = parseInt(routedPatientId)
    if (isNaN(patientId)) return

    const fileIds = folderContent
      .filter((item) => item.type === 'file')
      .map((item) => item.id)

    if (fileIds.length === 0) return

    try {
      const newVisibilityMap = new Map<number, boolean>()

      // Check visibility for each file
      for (const fileId of fileIds) {
        const assignedPatients = await getPatientsAssignedToFile(fileId)
        const isVisible = assignedPatients.some(
          (patient: any) => patient.patientId === patientId
        )
        newVisibilityMap.set(fileId, isVisible)
      }

      setFileVisibility(newVisibilityMap)
    } catch (error) {
      console.error('Error loading file visibility:', error)
    }
  }

  // Function to update file visibility
  const updateFileVisibility = async (fileId: number, isVisible: boolean) => {
    let patientId = 0
    if (routedPatientId) {
      patientId = parseInt(routedPatientId)
      if (!patientId) {
        showAlert('Invalid patient ID', 'error')
        return
      }
    } else {
      showAlert('No patient ID found.', 'error')
    }

    setUpdatingVisibility((prev) => new Set(prev).add(fileId))

    try {
      const request = {
        fileId,
        patientIds: [patientId],
      }

      if (isVisible) {
        await markFileVisibleToPatient(request)
      } else {
        await unassignFileFromPatients(request)
      }

      setFileVisibility((prev) => {
        const newMap = new Map(prev)
        newMap.set(fileId, isVisible)
        return newMap
      })

      // Update the current folder content to reflect the change
      setCurrentFolderContent((prevContent) => {
        if (!prevContent) return prevContent

        return prevContent.map((item) => {
          if (item.type === 'file' && item.id === fileId) {
            return { ...item, isVisibleToPatient: isVisible } as ResourceFile
          }
          return item
        })
      })

      showAlert(
        `File ${
          isVisible ? 'made visible to' : 'hidden from'
        } patient successfully`,
        'success'
      )
    } catch (error) {
      console.error('Error updating file visibility:', error)
      showAlert(
        `Failed to ${isVisible ? 'show' : 'hide'} file for patient`,
        'error'
      )
    } finally {
      setUpdatingVisibility((prev) => {
        const newSet = new Set(prev)
        newSet.delete(fileId)
        return newSet
      })
    }
  }

  const fetchFolderContent = async (folderId: number) => {
    setLoading(true)
    try {
      const folderDataResponse = await getFolderContentById(folderId)

      if (isMounted.current) {
        setCurrentFolder(folderDataResponse.currentFolder)
        setCurrentFolderContent(folderDataResponse.contents)
        setBreadcrumbPath(folderDataResponse.breadcrumbPath)

        // Load visibility information if viewing for a specific patient
        if (routedPatientId) {
          await loadFileVisibility(folderDataResponse.contents)
        }

        setLoading(false)
      }
    } catch (error) {
      console.error(`Error fetching folder ${folderId}:`, error)
      showAlert('Error loading folder contents', 'error')
      setLoading(false)
    }
  }

  const handleBreadcrumbClick = async (index: number) => {
    setLoading(true)
    if (index === 0) {
      if (selectedFile) {
        setSelectedFile(null)
      }

      await getRootResourceFolders()

      setCurrentFolder(null)
      setCurrentFolderContent(null)
      setBreadcrumbPath([])
      setLoading(false)
    } else {
      // Click on a folder in the breadcrumb path
      const folder = breadcrumbPath[index - 1]
      setBreadcrumbPath(breadcrumbPath.slice(0, -1))

      fetchFolderContent(folder.id)
    }
  }

  const renderBreadcrumbs = () => {
    return (
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize='small' />}
        aria-label='resource-navigation'
      >
        <Button
          color='inherit'
          onClick={() => handleBreadcrumbClick(0)}
          sx={{
            textTransform: 'none',
            textAlign: 'center',
            minWidth: 'auto',
            margin: 0,
            paddingX: 1,
            paddingY: 0,
          }}
        >
          <Typography>Resources</Typography>
        </Button>
        {breadcrumbPath.map((folder, index) => {
          const isLast = index === breadcrumbPath.length - 1
          return isLast ? (
            <Button
              color='inherit'
              disabled
              key={folder.id}
              sx={{
                textTransform: 'none',
                textAlign: 'center',
                minWidth: 'auto',
                margin: 0,
                paddingY: 0,
              }}
            >
              <Typography
                key={`breadcrumbPath-${folder.id}`}
                color={themeColors.gray40}
                paddingY={0}
                textAlign={'center'}
              >
                {folder.name}
              </Typography>
            </Button>
          ) : (
            <Button
              color='inherit'
              onClick={() => handleBreadcrumbClick(index + 1)}
              key={folder.id}
              sx={{
                textTransform: 'none',
                textAlign: 'center',
                minWidth: 'auto',
                margin: 0,
                paddingX: 1,
                paddingY: 0,
              }}
            >
              <Typography>{folder.name}</Typography>
            </Button>
          )
        })}
      </Breadcrumbs>
    )
  }

  const viewFile = async (file: any) => {
    try {
      const fileURL = await Storage.get(file.storageKey)
      window.open(fileURL, '_blank')
    } catch (error) {
      console.error('Error retrieving file:', error)
      showAlert('Error opening file. Please try again.', 'error')
    }
  }

  const renderResourceList = () => {
    if (loading || !rootResourceFolders) {
      return (
        <CircularProgress
          size={18}
          style={{ marginRight: 10, color: themeColors.gray50 }}
        />
      )
    }
    return (
      <List>
        {rootResourceFolders && currentFolder == null ? (
          rootResourceFolders.map((rootFolder) => (
            <Box
              key={`rootFolder-${rootFolder.id}`}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <ListItem
                button
                onClick={() => fetchFolderContent(rootFolder.id)}
              >
                <ListItemIcon>
                  <FolderIcon color='primary' />
                </ListItemIcon>
                <ListItemText primary={rootFolder.name} />
              </ListItem>
            </Box>
          ))
        ) : currentFolderContent && currentFolderContent.length > 0 ? (
          currentFolderContent.map((folderOrFile: ResourceItem) => (
            <Box
              key={`folderOrFile-${folderOrFile.id}`}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <ListItem
                button
                key={`folderOrFile-${folderOrFile.id}`}
                onClick={() =>
                  folderOrFile.type === 'folder'
                    ? fetchFolderContent(folderOrFile.id)
                    : setSelectedFile(folderOrFile)
                }
                sx={{ flex: 1 }}
              >
                <ListItemIcon>
                  {folderOrFile.type === 'folder' ? (
                    <FolderIcon color='primary' />
                  ) : (
                    <ArticleIcon color='primary' />
                  )}
                </ListItemIcon>
                <ListItemText primary={folderOrFile.name} />
              </ListItem>

              {folderOrFile.type === 'file' && routedPatientId && (
                <Box sx={{ marginRight: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={fileVisibility.get(folderOrFile.id) || false}
                        onChange={(e) => {
                          e.stopPropagation() // Prevent triggering the ListItem click
                          updateFileVisibility(
                            folderOrFile.id,
                            e.target.checked
                          )
                        }}
                        disabled={updatingVisibility.has(folderOrFile.id)}
                        size='small'
                      />
                    }
                    label={
                      updatingVisibility.has(folderOrFile.id) ? (
                        <CircularProgress size={12} />
                      ) : (
                        <Typography variant='caption' color='textSecondary'>
                          Visible
                        </Typography>
                      )
                    }
                    sx={{ margin: 0 }}
                  />
                </Box>
              )}
            </Box>
          ))
        ) : (
          <ListItem>
            <ListItemText primary='Currently Empty' />
          </ListItem>
        )}
      </List>
    )
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {alertMessage && (
        <Alert
          severity={alertSeverity}
          sx={{ mb: 2 }}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>{renderBreadcrumbs()}</Box>
      </Box>

      {/* view file page */}
      {selectedFile ? (
        <Box sx={{ p: 2 }}>
          <Typography variant='h6' gutterBottom padding={0}>
            Viewing: {selectedFile.name}
          </Typography>

          {/* Show visibility status for files when viewing for a specific patient */}
          {selectedFile.type === 'file' && routedPatientId && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fileVisibility.get(selectedFile.id) || false}
                    onChange={(e) => {
                      updateFileVisibility(selectedFile.id, e.target.checked)
                    }}
                    disabled={updatingVisibility.has(selectedFile.id)}
                  />
                }
                label={
                  <Typography variant='body2'>
                    {updatingVisibility.has(selectedFile.id)
                      ? 'Updating visibility...'
                      : 'Visible to this patient'}
                  </Typography>
                }
              />
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Button
              variant='outlined'
              color='primary'
              onClick={() => setSelectedFile(null)}
              sx={{ mt: 2, mr: 2 }}
            >
              Go Back
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={() => viewFile(selectedFile)}
              sx={{ mt: 2 }}
            >
              Open File
            </Button>
          </Box>
        </Box>
      ) : (
        // view folder page
        renderResourceList()
      )}
    </Box>
  )
}
